/**
 * Telehealth Join Page
 * 
 * Public page for both patients and providers to join telehealth appointments.
 * - Patients: Shows waiting room until provider joins, then pre-join screen
 * - Providers: Goes directly to pre-join screen (via role=provider param)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { VideoRoom, WaitingRoom, PreJoinScreen, EndCallStatusDialog } from '@/components/telehealth';
import type { PreJoinSettings } from '@/components/telehealth/PreJoinScreen';
import type { EndCallStatus } from '@/components/telehealth/EndCallStatusDialog';
import { telehealthService, TelehealthTokenResponse } from '@/services/telehealthService';
// Card components removed - using custom styled divs
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { ConsentGate } from '@/components/consent';

type ViewState = 'loading' | 'consent-check' | 'waiting' | 'fetching-token' | 'pre-join' | 'in-call' | 'ended' | 'error';

interface AppointmentInfo {
  patientId: string;
  clinicId: string;
  patientName?: string;
}

const PatientJoin: React.FC = () => {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const joinToken = searchParams.get('token') || '';
  const isProvider = searchParams.get('role') === 'provider';

  const [viewState, setViewState] = useState<ViewState>('loading');
  const [tokenData, setTokenData] = useState<TelehealthTokenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [appointmentInfo, setAppointmentInfo] = useState<AppointmentInfo | null>(null);
  
  // Pre-join device settings
  const [deviceSettings, setDeviceSettings] = useState<PreJoinSettings>({
    audioEnabled: true,
    videoEnabled: true,
  });

  // Post-call status dialog for providers
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  // Initial load - check if we can access the appointment
  useEffect(() => {
    const checkAppointment = async () => {
      if (!appointmentId || !joinToken) {
        setError('Invalid join link. Please check the link in your reminder email or text.');
        setViewState('error');
        return;
      }

      try {
        if (isProvider) {
          // For providers, go directly to fetching token (no waiting room)
          await fetchProviderToken();
        } else {
          // For patients, check waiting room status first and get appointment info
          const waitingRoomData = await telehealthService.checkWaitingRoom(appointmentId, joinToken);
          
          // Store appointment info for consent check if available
          if (waitingRoomData?.patientId && waitingRoomData?.clinicId) {
            setAppointmentInfo({
              patientId: waitingRoomData.patientId,
              clinicId: waitingRoomData.clinicId,
              patientName: waitingRoomData.patientName,
            });
            // Go to consent check first
            setViewState('consent-check');
          } else {
            // No consent info available, proceed to waiting room directly
            // (Backend may not have returned consent-related fields)
            setViewState('waiting');
          }
        }
      } catch (err) {
        console.error('Failed to check appointment:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : (err as { message?: string })?.message || 'Unable to access this appointment';
        setError(errorMessage);
        setViewState('error');
      }
    };

    checkAppointment();
  }, [appointmentId, joinToken, isProvider]);

  // Called when consent check is complete
  const handleConsentComplete = () => {
    setViewState('waiting');
  };

  // Called when patient cancels consent signing
  const handleConsentCancel = () => {
    // Navigate away or show a message
    setError('Consent forms are required to join your telehealth appointment.');
    setViewState('error');
  };

  // Fetch token for provider (token-based auth via join link)
  const fetchProviderToken = async () => {
    if (!appointmentId || !joinToken) return;

    setViewState('fetching-token');

    try {
      // Use the same endpoint but with provider role - backend validates token
      const data = await telehealthService.getJoinToken(appointmentId, joinToken, 'provider');
      setTokenData(data);
      setViewState('pre-join');
    } catch (err) {
      console.error('Failed to get provider token:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message
            || 'Failed to join video call. Please try logging in to the portal.';
      setError(errorMessage);
      setViewState('error');
    }
  };

  // Called when provider is ready and patient wants to join
  const handleProviderReady = async () => {
    if (!appointmentId || !joinToken) return;

    setViewState('fetching-token');

    try {
      const data = await telehealthService.getPatientToken(appointmentId, joinToken);
      setTokenData(data);
      setViewState('pre-join');
    } catch (err) {
      console.error('Failed to get token:', err);
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message || 'Failed to join video call';
      setError(errorMessage);
      setViewState('error');
    }
  };

  // Called when user completes pre-join and wants to enter the call
  const handlePreJoinComplete = (settings: PreJoinSettings) => {
    setDeviceSettings(settings);
    setViewState('in-call');
  };

  // Called when user cancels pre-join - go back to waiting room (patients only)
  const handlePreJoinCancel = () => {
    setViewState('ended');
  };

  const handleLeave = () => {
    if (isProvider) {
      // Show status dialog so provider can mark completed/no-show
      setShowStatusDialog(true);
    } else {
      setViewState('ended');
    }
  };

  // Called when provider confirms a status in the post-call dialog
  const handleStatusConfirm = async (status: EndCallStatus) => {
    if (appointmentId && joinToken) {
      try {
        await telehealthService.endRoomPublic(appointmentId, joinToken, status);
      } catch (err) {
        console.error('Failed to end room:', err);
      }
    }
    setShowStatusDialog(false);
    setViewState('ended');
  };

  const handleError = (error: Error) => {
    setError(error.message);
    setViewState('error');
  };

  const handleRetry = () => {
    setError(null);
    setViewState('loading');
    window.location.reload();
  };

  // Loading state
  if (viewState === 'loading' || viewState === 'fetching-token') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)]">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <Loader2 className="h-10 w-10 animate-spin text-brand-teal mb-4" />
        <p className="text-white text-base font-medium">
          {isProvider ? 'Preparing your video call...' : 'Loading your appointment...'}
        </p>
      </div>
    );
  }

  // Error state
  if (viewState === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)] p-4">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <div className="max-w-sm w-full bg-[#141c24] rounded-2xl border border-slate-700/50 p-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="text-lg font-semibold mb-2 text-white text-center">Unable to Join</h2>
          <p className="text-slate-400 text-center text-sm mb-5">{error}</p>
            <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleRetry} 
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-[#1a2530] hover:text-white bg-transparent"
            >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              {isProvider && (
              <Button 
                onClick={() => window.location.href = '/login'}
                className="bg-brand-teal hover:bg-brand-teal/90 text-white"
              >
                  Sign In to Portal
                </Button>
              )}
            </div>
        </div>
      </div>
    );
  }

  // Consent check (patients only) - check and prompt for required consents
  if (viewState === 'consent-check' && !isProvider && appointmentInfo) {
    return (
      <div className="min-h-screen bg-[rgb(10_15_20)] flex items-center justify-center p-4">
        <ConsentGate
          clinicId={appointmentInfo.clinicId}
          patientId={appointmentInfo.patientId}
          patientName={appointmentInfo.patientName}
          purpose="telehealth"
          onComplete={handleConsentComplete}
          onCancel={handleConsentCancel}
        />
      </div>
    );
  }

  // Waiting room (patients only)
  if (viewState === 'waiting' && !isProvider) {
    return (
      <WaitingRoom
        appointmentId={appointmentId!}
        joinToken={joinToken}
        onProviderReady={handleProviderReady}
        onError={(err) => {
          setError(err);
          setViewState('error');
        }}
        onLeave={handleLeave}
      />
    );
  }

  // Pre-join screen - camera/mic preview before entering call
  if (viewState === 'pre-join' && tokenData) {
    return (
      <PreJoinScreen
        onJoin={handlePreJoinComplete}
        onCancel={handlePreJoinCancel}
        displayName={tokenData.displayName}
        appointmentInfo={{
          providerName: isProvider ? tokenData.appointment.patientName : tokenData.appointment.providerName,
          startTime: tokenData.appointment.startTime,
        }}
        isProvider={isProvider}
      />
    );
  }

  // Call ended
  if (viewState === 'ended') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-[rgb(10_15_20)] p-4">
        <img src="/nimbus_logo_white.svg" alt="Nimbbus Providers" className="h-9 mb-10" />
        <div className="max-w-sm w-full bg-[#141c24] rounded-2xl border border-slate-700/50 p-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <svg className="h-7 w-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          <h2 className="text-lg font-semibold mb-2 text-white text-center">
              {isProvider ? 'Call Ended' : 'Visit Complete'}
            </h2>
          <p className="text-slate-400 text-center text-sm">
              {isProvider 
                ? 'The video call has ended. You may close this window.'
                : 'Thank you for your visit. You may now close this window.'
              }
            </p>
        </div>
      </div>
    );
  }

  // In-call state
  if (viewState === 'in-call' && tokenData) {
    return (
      <div className="h-screen w-screen">
        <VideoRoom
          roomUrl={tokenData.roomUrl}
          token={tokenData.token}
          displayName={tokenData.displayName}
          isProvider={isProvider}
          appointmentInfo={{
            providerName: isProvider ? tokenData.appointment.patientName : tokenData.appointment.providerName,
            startTime: tokenData.appointment.startTime,
            duration: tokenData.appointment.duration
          }}
          startVideoOff={!deviceSettings.videoEnabled}
          startAudioOff={!deviceSettings.audioEnabled}
          onLeave={handleLeave}
          onError={handleError}
        />

        {/* Post-call status dialog for providers */}
        {isProvider && (
          <EndCallStatusDialog
            open={showStatusDialog}
            onConfirm={handleStatusConfirm}
            patientName={tokenData.appointment.patientName}
          />
        )}
      </div>
    );
  }

  return null;
};

export default PatientJoin;
